import type { FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import EmptyState from '@atlaskit/empty-state';
import Link from '@atlaskit/link';
import { getConfig } from '../../../../config/envConfig';
import {
  useToken,
  useInfo,
  useSelectedWorkspace,
} from '../../../Flow/ConsentContextProvider';
import { useMcpContext } from '../McpContextProvider';
import { useMcpBranding } from '../mcpTerminology';
import { MCP_TRELLO_SUPPORT_URL } from '../../MCP/mcpUrls';
import ErrorImage from '#assets/error.svg';
import LockImage from '#assets/restricted.svg';
import SearchFailedImage from '#assets/search-failed.svg';
import { mcpPolicyAlertMessages } from '../../MCP/McpPolicyAlerts/messages';
import { MCP_POLICY_ERROR_KIND } from '../domain/policyState';

function resolveSiteLabel(
  selectedWorkspace: string | null | undefined,
  siteNameFromInfo: string | undefined,
  productUnit: string,
): string {
  const trimmed = selectedWorkspace?.trim();
  if (trimmed) return trimmed;
  if (siteNameFromInfo?.trim()) return siteNameFromInfo.trim();
  return productUnit;
}

const McpPolicyAlerts: FunctionComponent = () => {
  const token = useToken();
  const info = useInfo();
  const { formatMessage } = useIntl();
  const { selectedWorkspace } = useSelectedWorkspace();
  const {
    mcpPolicyErrorKind,
    mcpPolicyErrorWorkspaceName,
    scopeClassification,
  } = useMcpContext();
  const { productUnit } = useMcpBranding();

  const isTrelloOnly =
    scopeClassification.hasTrello && !scopeClassification.hasAtlassian;

  if (token.serviceType !== 'MCP') return null;
  // Keyed on the failure itself, not the combined status: that reports
  // `loading` whenever any check is in flight, so gating on it would hide an
  // already-failed check the moment the user touches another selector.
  if (!mcpPolicyErrorKind) return null;

  const redirectUrl = token.redirectUrl?.callback ?? '';
  const allowlistGuideUrl = getConfig().mcpPolicy.allowlistGuideUrl;
  const siteLabel = resolveSiteLabel(
    selectedWorkspace,
    info.siteName,
    productUnit,
  );

  if (mcpPolicyErrorKind === MCP_POLICY_ERROR_KIND.redirectNotAllowed) {
    return (
      <EmptyState
        header={formatMessage(mcpPolicyAlertMessages.domainNotAllowedTitle)}
        description={
          <FormattedMessage
            {...mcpPolicyAlertMessages.domainNotAllowedBodyLine1}
            values={{
              redirectUrl,
              siteLabel,
              allowlistLink: (text) => (
                <Link
                  href={allowlistGuideUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {text}
                </Link>
              ),
            }}
          />
        }
        imageUrl={LockImage}
      />
    );
  }

  const contactSupportUrl = isTrelloOnly
    ? MCP_TRELLO_SUPPORT_URL
    : getConfig().support.contactSupportUrl;

  if (mcpPolicyErrorKind === MCP_POLICY_ERROR_KIND.workspaceAccessRevoked) {
    return (
      <EmptyState
        header={formatMessage(
          mcpPolicyAlertMessages.workspaceAccessRevokedTitle,
        )}
        description={formatMessage(
          mcpPolicyAlertMessages.workspaceAccessRevokedBody,
          { workspaceName: mcpPolicyErrorWorkspaceName ?? productUnit },
        )}
        imageUrl={SearchFailedImage}
      />
    );
  }

  // Any failed request — a non-OK response or a throw — reads the same to the
  // user: we could not load their permissions. Which of the two it was tells
  // them nothing actionable, so both get one message.
  if (
    mcpPolicyErrorKind === MCP_POLICY_ERROR_KIND.policyFetchFailed ||
    mcpPolicyErrorKind === MCP_POLICY_ERROR_KIND.unexpected
  ) {
    return (
      <EmptyState
        header={formatMessage(mcpPolicyAlertMessages.somethingWentWrongTitle)}
        description={
          <FormattedMessage
            {...mcpPolicyAlertMessages.unexpectedBody}
            values={{
              supportLink: (text) => (
                <Link
                  href={contactSupportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {text}
                </Link>
              ),
            }}
          />
        }
        imageUrl={ErrorImage}
      />
    );
  }

  if (mcpPolicyErrorKind === MCP_POLICY_ERROR_KIND.missingRedirect) {
    return (
      <EmptyState
        header={formatMessage(mcpPolicyAlertMessages.missingRedirectTitle)}
        description={formatMessage(mcpPolicyAlertMessages.missingRedirectBody)}
        imageUrl={ErrorImage}
      />
    );
  }

  return null;
};

export default McpPolicyAlerts;
